import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserFileService } from 'src/app/files/service/user-file.service';
import { SequenceSuffixType } from 'src/app/shared/model/export-request-param';
import { notAllowedCharValidator } from 'src/app/shared/util/validators';

@Component({
  selector: 'app-report-filename-editor',
  templateUrl: './report-filename-editor.component.html',
  styleUrls: ['./report-filename-editor.component.scss']
})
export class ReportFilenameEditorComponent implements OnInit {

  constructor(private fileService: UserFileService, private translate: TranslateService) {}

  private scheduleEnabled: boolean = false;

  @Input() set isSchedule(isScheduleEnabled: boolean) {
    this.scheduleEnabled = isScheduleEnabled;
    this.refreshSuffixTypeControl();
  }

  get isSchedule(): boolean {
    return this.scheduleEnabled;
  }

  @Output() paramSelected: EventEmitter<string> = new EventEmitter();

  edit: boolean = false;
  suffixTypes: SequenceSuffixType[] = [SequenceSuffixType.SEQUENCE_TIMESTAMP, SequenceSuffixType.SEQUENCE_NUMBER];
  formGroup: FormGroup;

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      fileName: new FormControl(
          null,
          {
          updateOn: 'blur',
          validators: [(control) => notAllowedCharValidator(control, '${}'), Validators.required],
          asyncValidators: this.fileNameValidator
          }
      )
    });
    this.refreshSuffixTypeControl();
    this.formGroup.disable();
  }

  canSubmit(): boolean {
    return !this.edit;
  }

  cancel(): void {
    this.formGroup.disable();
    this.formGroup.reset();
    this.paramSelected.emit(null);
    this.edit = false;
  }

  getError(controlName: string): string {
    const control = this.formGroup?.controls[controlName];
    if (control.errors) {
      const error = Object.entries(control.errors)[0];
      const errorName = `${this.translate.instant(error[0])}`;
      return error[0] == 'notAllowedChars' ? `${errorName}: ${error[1]}` : errorName;
    }
  }

  resetFileNameValidation(event: InputEvent): void {
    if (this.formGroup.invalid) {
      const fileNameControl = this.formGroup?.controls?.fileName;
      const target = event.target as HTMLInputElement;
      fileNameControl?.reset(target.value);
      this.formGroup.reset(this.formGroup.getRawValue());
    }
  }

  confirm(): void {
    if (this.formGroup.valid) {
      this.paramSelected.emit(this.buildFileName());
      this.formGroup.disable();
      this.edit = false;
    }
  }

  enable(): void {
    this.formGroup.enable();
    this.edit = true;
  }

  private buildFileName(): string {
    const fileNameValue = this.formGroup.value.fileName;
    const suffixValue = this.formGroup.value.suffixType;
    const suffixTemplate = suffixValue ? '${'+ suffixValue + '}' : '';
    return fileNameValue + suffixTemplate;
  }

  private fileNameValidator = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (control.value) {
      return this.fileService.checkUserFileNameExists(control.value).pipe(map((data: boolean) => {
        if (data) {
          return { fileNameExists: true };
        }
        return null;
      }));
    }
    return of(null);
  };

  private refreshSuffixTypeControl(): void {
    if (this.isSchedule) {
      const isParentDisabled = this.formGroup?.disabled;
      this.formGroup?.addControl('suffixType', new FormControl(null, Validators.required));
      const control = this.formGroup?.controls.suffixType;
      if (isParentDisabled)
        control?.disable();
    }
    else
      this.formGroup?.removeControl('suffixType');
  }
}
